import React, { useEffect, useState } from "react";
import {
  FaSave,
  FaClock,
  FaRocket,
  FaTachometerAlt,
  FaPlane,
  FaCoins,
} from "react-icons/fa";
import notify from "../../utils/toast";
import PageHeader from "../../components/PageHeader";
import {
  getAviatorGameSettings,
  updateAviatorGameSettings,
} from "../../api/endpoints";

const AviatorSettings = () => {
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    _id: "",
    gameName: "AVIATOR",
    isActive: true,
    minBet: 10,
    maxBet: 100000,
    bettingTime: 10,
    planeStartDelay: 1,
    settlementDelay: 2,
    nextRoundDelay: 3,
    multiplierSpeed: 100,
    multiplierStep: 0.01,
    minCrashPoint: 1.01,
    maxCrashPoint: 100,
    maxMultiplier: 100,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await getAviatorGameSettings();
      if (res.success && res.data) {
        setSettings(res.data);
      }
    } catch (err) {
      console.error(err);
      notify.error(err, "Could not load the Aviator settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
    }));
  };

  const handleSave = async () => {
    // Validation
    if (settings.minBet >= settings.maxBet) {
      notify.warning("Minimum bet must be lower than maximum bet.", "Check the bet range");
      return;
    }

    if (settings.minCrashPoint >= settings.maxCrashPoint) {
      notify.warning("Minimum crash point must be lower than the maximum.", "Check the crash range");
      return;
    }

    try {
      setLoading(true);
      const res = await updateAviatorGameSettings(settings);
      if (res.success) {
        notify.success(res.message || "Aviator settings are live.", "Settings saved");
        fetchSettings();
      } else {
        notify.error(res.message, "Could not save these settings");
      }
    } catch (err) {
      console.error(err);
      notify.error(err, "Could not save these settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page lead-page">
      <div className="row">
        <div className="col-lg-12">
          <PageHeader
            title="Aviator Settings"
            subtitle="Manage Aviator game settings"
          />

          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body">
              <div className="row">
                {/* Game Name */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Game Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="gameName"
                    value={settings.gameName}
                    onChange={handleChange}
                    disabled
                  />
                </div>

                {/* Status */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Status</label>
                  <div className="form-check form-switch mt-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      name="isActive"
                      checked={settings.isActive}
                      onChange={handleChange}
                      style={{ width: "3em", height: "1.5em" }}
                    />
                    <label className="form-check-label ms-2">
                      <span className={`badge ${settings.isActive ? "bg-success" : "bg-danger"}`}>
                        {settings.isActive ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Timing Settings */}
                <div className="col-12">
                  <h6 className="fw-bold mb-3 border-bottom pb-2">
                    <FaClock className="me-2 text-primary" />
                    Timing Settings
                  </h6>
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    <FaClock className="me-1" />
                    Betting Time (sec)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="bettingTime"
                    value={settings.bettingTime}
                    onChange={handleChange}
                    min="1"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    <FaRocket className="me-1" />
                    Plane Start Delay (sec)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="planeStartDelay"
                    value={settings.planeStartDelay}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Settlement Delay (sec)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="settlementDelay"
                    value={settings.settlementDelay}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Next Round Delay (sec)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="nextRoundDelay"
                    value={settings.nextRoundDelay}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                  />
                </div>

                {/* Multiplier Settings */}
                <div className="col-12">
                  <h6 className="fw-bold mb-3 border-bottom pb-2">
                    <FaTachometerAlt className="me-2 text-primary" />
                    Multiplier Settings
                  </h6>
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Multiplier Speed
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="multiplierSpeed"
                    value={settings.multiplierSpeed}
                    onChange={handleChange}
                    min="1"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Multiplier Step
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="multiplierStep"
                    value={settings.multiplierStep}
                    onChange={handleChange}
                    min="0.01"
                    step="0.01"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Min Crash Point
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="minCrashPoint"
                    value={settings.minCrashPoint}
                    onChange={handleChange}
                    min="1"
                    step="0.01"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Max Crash Point
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="maxCrashPoint"
                    value={settings.maxCrashPoint}
                    onChange={handleChange}
                    min="1"
                    step="0.01"
                  />
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold">
                    Max Multiplier
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    name="maxMultiplier"
                    value={settings.maxMultiplier}
                    onChange={handleChange}
                    min="1"
                  />
                </div>

                {/* Betting Limits */}
                <div className="col-12">
                  <h6 className="fw-bold mb-3 border-bottom pb-2">
                    <FaCoins className="me-2 text-primary" />
                    Betting Limits
                  </h6>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Minimum Bet</label>
                  <div className="input-group">
                    <span className="input-group-text">₹</span>
                    <input
                      type="number"
                      className="form-control"
                      name="minBet"
                      value={settings.minBet}
                      onChange={handleChange}
                      min="1"
                    />
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Maximum Bet</label>
                  <div className="input-group">
                    <span className="input-group-text">₹</span>
                    <input
                      type="number"
                      className="form-control"
                      name="maxBet"
                      value={settings.maxBet}
                      onChange={handleChange}
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div className="text-end mt-3 pt-3 border-top">
                <button
                  className="btn btn-primary px-5 py-2"
                  onClick={handleSave}
                  disabled={loading}
                >
                  <FaSave className="me-2" />
                  {loading ? "Saving..." : "Save Settings"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AviatorSettings;